import React from 'react'

import { Icon } from '@factorialco/deprecated-design-system'
import { useXRay } from '@factorialco/factorial-one-react'
import { EnvironmentContext } from 'config/environment'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useTextSearch } from 'modules/core/hooks/useTextSearch'
import { useClientSideFeatureFlag } from 'modules/core/lib/clientSideFeatureFlags'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { useLayoutContext } from 'modules/layout/hooks/useFullscreen'

import type { ResultType, SearchPlugin, SearchPluginHook } from './types'

const FullScreenIcon = () => <Icon.Medium icon="code" />
const PlaygroundIcon = () => <Icon.Medium icon="heart" />
const DesignModeIcon = () => <Icon.Medium icon="pencil" />
const XRayIcon = () => <Icon.Medium icon="ai" />

const disableClickHandler = (event: MouseEvent) => {
  event.stopPropagation()
  event.preventDefault()
}

const useDevtools: SearchPluginHook = (search) => {
  const { toggleFullScreen, fullScreenEnabled } = useLayoutContext()
  const { devtoolsEnabled } = React.useContext(EnvironmentContext)
  const { navigation } = useNavigation()
  const { featureFlags, isFeatureFlagEnabled, toggleFeatureFlag } = useClientSideFeatureFlag()

  const { enabled: xRayEnabled, enable: enableXRay, disable: disableXRay } = useXRay()

  const isEnabled = devtoolsEnabled || hasFeature('dev_command_bar_devtools')

  const devtoolsActions: ResultType[] = [
    ...featureFlags.map((flag) => ({
      id: flag,
      text: `${isFeatureFlagEnabled(flag) ? 'Disable' : 'Enable'} ${flag}`,
      action: () => toggleFeatureFlag(flag),
      IconComponent: FullScreenIcon,
      score: 1,
      type: 'action' as const,
    })),
    {
      id: 'toggle-fullscreen',
      text: 'Toggle Full Screen',
      action: () => toggleFullScreen(),
      IconComponent: FullScreenIcon,
      score: 1,
      type: 'action',
      filter: fullScreenEnabled,
    },
    {
      id: 'factorial-one-xray',
      text: 'Toggle Factorial One XRay',
      action: () => (xRayEnabled ? disableXRay() : enableXRay()),
      IconComponent: XRayIcon,
      score: 2,
      type: 'action',
    },
    {
      id: 'playground',
      text: 'Playground',
      navigationPath: navigation.playground.path,
      IconComponent: PlaygroundIcon,
      score: 3,
      type: 'navigation',
    },
    {
      id: 'design-mode',
      text: document.designMode === 'on' ? 'Disable design Mode' : 'Enable design mode',
      action: () => {
        if (document.designMode === 'off' || document.designMode === undefined) {
          document.designMode = 'on'
          document.addEventListener('click', disableClickHandler, true)
        } else {
          document.designMode = 'off'
          document.removeEventListener('click', disableClickHandler, true)
        }
      },
      IconComponent: DesignModeIcon,
      score: 3,
      type: 'action',
    },
  ]

  const { results: searchResults } = useTextSearch(devtoolsActions, search, {
    fields: ['text'],
    storeFields: ['id'],
    fuzzy: 0.1,
  })

  const results = isEnabled
    ? search.length > 0
      ? searchResults
          .map((result) => devtoolsActions.find((action) => action.id === result.id))
          .filter((result): result is ResultType => !!result)
      : devtoolsActions
    : []

  return {
    id: 'devtools',
    results,
    title: i18n.t('command_bar.plugins.devtools.title'),
    loading: false,
  }
}

export const devtools: SearchPlugin = {
  hook: useDevtools,
}
